import { Component, Input, OnDestroy, OnInit } from '@angular/core';
import {
  FormArray,
  FormBuilder,
  FormControl,
  FormGroup,
  Validators
} from '@angular/forms';
import { NzDrawerRef } from 'ng-zorro-antd/drawer';
import { mapValues } from 'lodash';
import {
  Drug,
  DrugDTO,
  IPharmacy,
  ISelectOption,
  mitteTypeList
} from '@care-portals/api-interfaces';
import { DrugFacade } from '../../../+store/drug/drug.facade';
import { capitalCase } from 'change-case';
import { filter, takeWhile } from 'rxjs/operators';
import { DrugStatuses } from '@care-portals/data';
import { APIService } from '../../../services/api/api.service';

interface DrugOption {
  din: string;
  mitte: number;
  retailPrice: number;
  type: 'one-time' | 'subscription';
}

@Component({
  selector: 'my-health-admin-drug-detail',
  templateUrl: './drug-detail.component.html',
  styleUrls: ['./drug-detail.component.less']
})
export class DrugDetailComponent implements OnInit, OnDestroy {
  @Input() din: string;
  drug: Drug | null = null;
  drugForm!: FormGroup;
  saving: boolean;
  loading = false;
  loadError: string | null = null;
  mitteTypeOptions: ISelectOption[];
  statusOptions = DrugStatuses;
  isStart = true;
  active = true;
  pharmaciesOptions: IPharmacy[] = [];

  constructor(
    private formBuilder: FormBuilder,
    private drugFacade: DrugFacade,
    private nzDrawerRef: NzDrawerRef,
    private apiService: APIService
  ) {
    this.drugFacade.saving$.subscribe(res => {
      this.saving = res;
      if (!res && !this.isStart) {
        this.nzDrawerRef.close(true);
      }
    });
  }

  ngOnInit(): void {
    this.isStart = false;
    this.active = true;

    this.mitteTypeOptions = mitteTypeList.map(type => ({
      value: type,
      label: capitalCase(type)
    }));

    this.drugForm = this.formBuilder.group({
      din: ['', Validators.required],
      ndc: [''],
      name: ['', Validators.required],
      mitteType: ['', Validators.required],
      options: new FormArray([]),
      price: [0],
      sig: [],
      mitte: [],
      refills: [],
      products: [[]],
      categories: [[]],
      strength: [],
      status: ['active', Validators.required],
      pharmacy: [null],
      extras: ['']
    });

    this.apiService
      .getPharmacies()
      .pipe(takeWhile(() => this.active))
      .subscribe(pharmacies => {
        this.pharmaciesOptions = (pharmacies || []).filter(
          p => !!(p._id || p.belugaId)
        );
        // Drug may have loaded before pharmacies; re-resolve selection now
        this.syncPharmacyFormValue();
      });

    if (this.din) {
      this.drugFacade.loadDrugByDin(this.din);

      this.drugFacade.drugDetailLoading$
        .pipe(takeWhile(() => this.active))
        .subscribe(loading => (this.loading = loading));

      this.drugFacade.drugDetailError$
        .pipe(takeWhile(() => this.active))
        .subscribe(error => (this.loadError = error));

      this.drugFacade.drugDetail$
        .pipe(
          takeWhile(() => this.active),
          filter((drug): drug is Drug => drug !== null && drug.din === this.din)
        )
        .subscribe(drug => {
          this.drug = drug;
          this.drugForm.patchValue({
            ...drug,
            pharmacy: this.resolvePharmacyFormValue(drug.pharmacy)
          });
          if (drug.options?.length) {
            this.optionsControl.clear();
            drug.options.forEach(option => {
              this.addDrugOption(option as any);
            });
          }
        });
    } else {
      this.drugFacade.clearDrugDetail();
    }
  }

  ngOnDestroy(): void {
    this.active = false;
    this.drugFacade.clearDrugDetail();
  }

  get optionsControl(): FormArray {
    return this.drugForm.get('options') as FormArray;
  }

  /** Stable nz-select value: prefer `_id`, fall back to `belugaId` for legacy. */
  getPharmacySelectValue(pharmacy: IPharmacy): string {
    return String(pharmacy?._id || pharmacy?.belugaId || '');
  }

  /**
   * Map stored drug.pharmacy (`_id` or legacy `belugaId`) to the select value.
   */
  private resolvePharmacyFormValue(
    stored: string | null | undefined
  ): string | null {
    if (!stored) return null;
    if (!this.pharmaciesOptions.length) return String(stored);

    const storedStr = String(stored);
    const match = this.pharmaciesOptions.find(p => {
      const id = p._id != null && p._id !== '' ? String(p._id) : '';
      const belugaId = p.belugaId ? String(p.belugaId) : '';
      return id === storedStr || belugaId === storedStr;
    });

    if (!match) return storedStr;
    return this.getPharmacySelectValue(match) || storedStr;
  }

  private syncPharmacyFormValue() {
    const stored = this.drug?.pharmacy ?? this.drugForm?.get('pharmacy')?.value;
    if (!stored) return;
    const resolved = this.resolvePharmacyFormValue(stored);
    this.drugForm.patchValue({ pharmacy: resolved });
  }

  addDrugOption(option: DrugOption) {
    this.optionsControl.insert(
      this.optionsControl.controls.length,
      new FormGroup({
        din: new FormControl(option?.din || '', []),
        mitte: new FormControl(option?.mitte || 0, [
          Validators.required,
          Validators.pattern('^[0-9]+(\\.[0-9]{1,2})?$')
        ]),
        retailPrice: new FormControl(option?.retailPrice || 0, [
          Validators.pattern('^[0-9]*$')
        ]),
        type: new FormControl(option?.type || 'one-time', [])
      })
    );
  }

  deleteOption(index: number): void {
    this.optionsControl.removeAt(index);
  }

  public static touchAll(formGroup: FormGroup | FormArray): void {
    mapValues(formGroup.controls, (c, i) => {
      if (c instanceof FormGroup || c instanceof FormArray) {
        this.touchAll(c);
      } else {
        c['markAsDirty']();
        c['updateValueAndValidity']();
      }
    });
  }

  saveRx() {
    DrugDetailComponent.touchAll(this.optionsControl);

    if (!this.drugForm.valid) {
      return;
    }
    const value = this.drugForm.value;
    // Persist pharmacy `_id`; null clears the optional field
    const pharmacy = value.pharmacy || null;

    if (!this.drug) {
      const newDrug: DrugDTO = {
        din: value.din,
        ndc: value.ndc,
        name: value.name,
        mitteType: value.mitteType,
        options: value.options,
        price: value.price,
        sig: value.sig,
        mitte: value.mitte,
        refills: value.refills,
        products: value.products,
        categories: value.categories,
        status: value.status,
        strength: value.strength,
        pharmacy,
        extras: value.extras
      };

      this.drugFacade.saveDrug(newDrug);
    } else {
      const updatedDrug: DrugDTO = {
        din: value.din,
        ndc: value.ndc,
        name: value.name,
        mitteType: value.mitteType,
        options: value.options,
        price: value.price,
        sig: value.sig,
        mitte: value.mitte,
        refills: value.refills,
        products: value.products,
        categories: value.categories,
        status: value.status,
        strength: value.strength,
        pharmacy,
        extras: value.extras
      };

      this.drugFacade.updateDrug(this.drug._id, updatedDrug);
    }
  }

  close(): void {
    this.nzDrawerRef.close(true);
  }
}
